import {ArrowRight, Loader2, Mail} from "lucide-react";
import InputGroup from "../../../ui/fields/InputGroup";
import useKeyboardReveal from "../hooks/useKeyboardReveal";

const LoginFormView = ({
  loginState,
  formData,
  getE164Phone,
  handleContinue,
  handlePhoneChange,
  handleInputChange,
  authMethod,
  isActionLoading,
  rememberDevice = true,
  setRememberDevice = () => {},
}) => {
  // iOS Safari doesn't pan the locked-document layout to a newly focused
  // field; this hook does it manually. Spread fieldProps on each input,
  // put actionRef on the Continue button (it scrolls just enough to lift
  // that above the keyboard), and render the spacer at the end of the
  // form. The hook sizes the spacer imperatively, so no style prop here.
  const {fieldProps, spacerRef, actionRef} = useKeyboardReveal();
  const keyboardSpacer = <div aria-hidden="true" ref={spacerRef} />;

  const isEmailLogin = loginState.state === "login" && authMethod === "email";
  const isPhoneReady =
    getE164Phone(formData.phone).replace(formData.country_dialcode, "")
      .length === 10;
  const isEmailReady =
    formData.email.trim() !== "" &&
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim());

  // Real <form> + type="submit" so the mobile keyboard's Go/Enter key
  // submits exactly like tapping Continue. While the button is disabled
  // the browser skips implicit submission, but the guard also covers
  // Enter pressed during an in-flight request.
  const submitContinue = (ready) => (e) => {
    e.preventDefault();
    if (ready && !isActionLoading) handleContinue();
  };

  // Same bulk-entry rule as the OTP screen: a value delivered in one action
  // (OS autofill, keyboard suggestion chip, paste) continues immediately when
  // it's complete -- those sources don't typo. Typed input keeps the explicit
  // Continue tap. The fresh value goes to handleContinue directly; reading it
  // back from state would race the re-render. Bulk = the value grew by more
  // than one character in a single input event.
  const digitsOf = (s) => (s || "").replace(/\D/g, "");
  const maybeAutoContinue = (e, prevValue) => {
    if (isActionLoading) return;
    const next = e.target.value || "";
    const inputType = e.nativeEvent?.inputType || "";
    const bulk =
      inputType === "insertFromPaste" ||
      inputType === "insertReplacementText" ||
      (isEmailLogin
        ? next.length - (prevValue || "").length > 1
        : digitsOf(next).length - digitsOf(prevValue).length > 1);
    if (!bulk) return;
    if (isEmailLogin) {
      const email = next.trim();
      if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) handleContinue(email);
      return;
    }
    // Mirror handlePhoneChange's normalization: strip to digits, drop a
    // leading country-code 1 from an 11-digit number.
    let digits = digitsOf(next).slice(0, 15);
    if (digits.length > 10 && digits.startsWith("1")) digits = digits.slice(1);
    if (digits.length === 10) handleContinue(digits);
  };

  // "Keep me signed in", checked by default (opt-out). Checked persists the
  // session in localStorage up to the refresh token's ~30-day lifetime, so the
  // learner isn't re-prompted for a code every visit; unchecking falls back to
  // sessionStorage (cleared on browser close). One line of copy, no helper text.
  const rememberField = (
    <label className="mt-4 flex items-center gap-3 px-2 cursor-pointer">
      <input
        type="checkbox"
        checked={rememberDevice}
        onChange={(e) => setRememberDevice(e.target.checked)}
        className="h-5 w-5 shrink-0 rounded border-gray-300 text-[#001489] focus:ring-2 focus:ring-indigo-400"
      />
      <span className="text-sm font-medium text-[#191919]">
        Keep me signed in on this device
      </span>
    </label>
  );

  const signInTip = (
    <p className="px-2 text-sm font-medium text-[#001489]">
      <strong className="font-bold">* Important:</strong> Sign in with the phone
      number or email you got the link from, or your watch time won't count.
    </p>
  );

  // 10DLC / carrier SMS opt-in disclosure. Required at the point we collect the
  // number and trigger the verification text (US A2P messaging rules + project
  // requirement); shown only on the phone path since email sends no SMS. The
  // carrier strings (rates / frequency / HELP / STOP / ESP) are verbatim and
  // must not be edited. Links target idaho.communitycx.com because
  // idahoemploymenttraining.com still serves the outgoing operator's site;
  // swap the host when that domain cuts over. text-gray-500 meets WCAG AA
  // on white for text this size.
  const smsConsent = (
    <p className="mt-3 px-2 text-xs leading-snug text-gray-500">
      By continuing, you agree to get a one-time login code by text from Idaho
      Employment &amp; Training. Message and data rates may apply. Message
      frequency varies. Reply HELP for help. Reply STOP to opt out. Responda ESP
      para español. See our{" "}
      <a
        href="https://idaho.communitycx.com/sms-terms"
        target="_blank"
        rel="noopener noreferrer"
        className="text-[#001489] underline"
      >
        Text Message Policy
      </a>{" "}
      and{" "}
      <a
        href="https://idaho.communitycx.com/privacy"
        target="_blank"
        rel="noopener noreferrer"
        className="text-[#001489] underline"
      >
        Privacy Policy
      </a>
      .
    </p>
  );

  // Same in-flight feedback as the OTP screen's Verify button. Matters most
  // on the bulk-entry path above, where the request fires without a tap and
  // the button is the only signal anything is happening.
  const continueLabel = isActionLoading ? (
    <>
      <Loader2 size={20} className="animate-spin" /> Sending code...
    </>
  ) : (
    <>
      Continue <ArrowRight size={20} className="inline" />
    </>
  );
  const continueClasses = (ready) =>
    `w-full py-4 md:py-3 rounded-full text-white text-lg md:text-xl font-bold transition-colors flex items-center justify-center gap-2 ${
      isActionLoading
        ? "bg-[#b3b3b3] cursor-wait"
        : ready
          ? "bg-black hover:bg-gray-800 shadow-lg"
          : "bg-[#b3b3b3] cursor-not-allowed"
    }`;

  if (isEmailLogin) {
    return (
      <form
        className="w-full max-w-md flex-1 md:flex-none"
        onSubmit={submitContinue(isEmailReady)}
        noValidate
      >
        <div className="space-y-2">
          {signInTip}
          <div className="space-y-3">
            <div className="relative">
              <Mail
                className="absolute left-4 top-3.5 text-gray-300"
                size={20}
              />
              <input
                id="email"
                type="email"
                placeholder="Enter your email"
                value={formData.email}
                autoComplete="email"
                onChange={(e) => {
                  handleInputChange(e);
                  maybeAutoContinue(e, formData.email);
                }}
                {...fieldProps}
                className="w-full pl-12 pr-4 py-3 bg-white border border-gray-100 md:border-gray-200 rounded-[20px] md:rounded-full outline-none focus:ring-2 focus:ring-indigo-400"
              />
            </div>
          </div>
        </div>

        {rememberField}

        {/* In normal flow (not fixed): the iOS keyboard overlays the page
          instead of resizing it, so a bottom-pinned button hides behind
          the keypad while typing. */}
        <div className="w-full mt-6">
          <button
            ref={actionRef}
            type="submit"
            disabled={!isEmailReady || isActionLoading}
            className={continueClasses(isEmailReady)}
          >
            {continueLabel}
          </button>
        </div>
        {keyboardSpacer}
      </form>
    );
  }

  return (
    <form
      className="w-full max-w-md flex-1 md:flex-none"
      onSubmit={submitContinue(isPhoneReady)}
      noValidate
    >
      <div className="space-y-2">
        {signInTip}
        <div className="flex items-center border border-gray-100 md:border-gray-200 rounded-[20px] md:rounded-full px-4 py-2 md:py-1 focus-within:ring-2 focus:ring-indigo-400">
          <InputGroup
            id="phone"
            type="tel"
            placeholder="Enter your phone number"
            value={formData.phone}
            autoComplete="tel"
            onChange={(e) => {
              handlePhoneChange(e);
              maybeAutoContinue(e, formData.phone);
            }}
            {...fieldProps}
          />
        </div>
      </div>
      {rememberField}
      {/* In normal flow (not fixed): the iOS keyboard overlays the page
          instead of resizing it, so a bottom-pinned button hides behind
          the keypad while typing. */}
      <div className="w-full mt-6">
        <button
          ref={actionRef}
          type="submit"
          disabled={!isPhoneReady || isActionLoading}
          className={continueClasses(isPhoneReady)}
        >
          {continueLabel}
        </button>
      </div>
      {smsConsent}
      {keyboardSpacer}
    </form>
  );
};

export default LoginFormView;
